/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import {
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Box,
  LinearProgress,
} from '@mui/material';
import React from 'react';
import {
  AlertBanner,
  AlertIcon,
  ToolContainerDraggable,
  TooltipSelect,
  referenceTimeUtils,
} from '@opengeoweb/shared';
import type { SourceSelection } from '@opengeoweb/shared';
import { getVerticalRangeFromInstance } from '../../api/edr-api/utils';
import { useSoundingsTranslation } from '../../utils/i18n';
import type { SoundingsCollectionsWithInstances } from '../Soundings/types';
import { getDisplayValue } from '../utils';

export const defaultInstanceId = 'latest-0';

const extractVerticalRange = (
  collection: SoundingsCollectionsWithInstances,
  selectedInstanceId: string,
): string =>
  getVerticalRangeFromInstance(
    collection.instances.find((instance) => instance.id === selectedInstanceId),
  );

const resolveInstanceId = (
  collection: SoundingsCollectionsWithInstances,
  selectedValue: string,
): string =>
  referenceTimeUtils.resolveReferenceTimeInstance(
    collection.instances,
    selectedValue,
  )?.id ?? selectedValue;

const getSelectedDisplayValue = (value: string): string => {
  const offset = referenceTimeUtils.parseRelativeReferenceTimeOffset(value);
  return offset !== null
    ? referenceTimeUtils.formatRelativeReferenceTimeLabel(offset)
    : getDisplayValue(value);
};

interface SoundingsSourceMenuProps {
  soundingsCollections: SoundingsCollectionsWithInstances[] | undefined;
  isOpen?: boolean;
  handleClose: () => void;
  updateCollection: (sourceId: string, value: SourceSelection) => void;
  sourceSelections: Record<string, SourceSelection>;
  onMouseDown?: () => void;
  order?: number;
  isLoading?: boolean;
  error?: Error | null;
  startSize?: { width?: number; height?: number };
}

export const SoundingsSourceMenu = ({
  soundingsCollections,
  isOpen = false,
  handleClose,
  updateCollection,
  sourceSelections,
  onMouseDown = (): void => {},
  order = 0,
  isLoading = false,
  error,
  startSize = { width: 320 },
}: SoundingsSourceMenuProps): React.ReactElement => {
  const { t } = useSoundingsTranslation();

  return (
    <ToolContainerDraggable
      bounds="parent"
      className="source-menu"
      isOpen={isOpen}
      minWidth={300}
      onClose={handleClose}
      onMouseDown={onMouseDown}
      order={order}
      startPosition={{ top: 40, left: 50 }}
      startSize={startSize}
      title={t('sources')}
    >
      {!!isLoading && (
        <LinearProgress sx={{ width: '100%', position: 'absolute' }} />
      )}
      {!!error && <AlertBanner shouldClose title={error.message} />}
      <List sx={{ pr: 1 }}>
        {soundingsCollections && soundingsCollections.length > 0 ? (
          soundingsCollections.map((collection) => {
            const { isChecked, instanceId, url } =
              sourceSelections[collection.id] || {};
            const sourceUrl = url || collection.url;
            const hasInstances = collection.instances.length > 0;
            const selectedInstanceId = hasInstances
              ? instanceId || defaultInstanceId
              : undefined;

            const hasNonExistingInstanceId =
              selectedInstanceId &&
              !referenceTimeUtils.isRelativeReferenceTime(selectedInstanceId) &&
              !collection.instances.some(
                (instance) => instance.id === selectedInstanceId,
              );
            return (
              <ListItem disablePadding key={collection.id}>
                <ListItemIcon
                  sx={{
                    minWidth: 42,
                  }}
                >
                  <Checkbox
                    checked={!!isChecked}
                    disableRipple
                    onChange={(event) => {
                      const verticalRange =
                        selectedInstanceId !== undefined
                          ? extractVerticalRange(
                              collection,
                              resolveInstanceId(collection, selectedInstanceId),
                            )
                          : '';

                      updateCollection(collection.id, {
                        url: sourceUrl,
                        isChecked: event.target.checked,
                        instanceId: selectedInstanceId,
                        verticalRange,
                      });
                    }}
                  />
                </ListItemIcon>
                <TooltipSelect
                  additionalStyling={{ width: 'calc(100% - 42px)' }}
                  isEnabled
                  onChange={(event: SelectChangeEvent<unknown>) => {
                    const selectedValue = (event as SelectChangeEvent<string>)
                      .target.value;
                    const verticalRange = extractVerticalRange(
                      collection,
                      resolveInstanceId(collection, selectedValue),
                    );
                    updateCollection(collection.id, {
                      url: sourceUrl,
                      instanceId: selectedValue,
                      verticalRange,
                    });
                  }}
                  renderValue={(value: unknown) => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          flex: '4 1 0',
                          minWidth: 0,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {collection.name}
                      </Box>

                      <Box
                        sx={{
                          flex: '1 1 0',
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: 0,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getSelectedDisplayValue(value as string)}
                        </Box>
                        {hasNonExistingInstanceId ? (
                          <Box
                            sx={{
                              '& .MuiSvgIcon-root': {
                                fill: 'currentcolor!important',
                              },
                            }}
                          >
                            <AlertIcon severity="error" />
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  )}
                  skipLocalStyling={false}
                  tooltip={`${collection.name}: ${getSelectedDisplayValue(
                    selectedInstanceId || t('noInstancesAvailable'),
                  )}`}
                  value={selectedInstanceId || t('noInstancesAvailable')}
                >
                  {!hasInstances && (
                    <MenuItem
                      disabled
                      sx={{ fontSize: 12 }}
                      value={t('noInstancesAvailable')}
                    >
                      {t('noInstancesAvailable')}
                    </MenuItem>
                  )}
                  {referenceTimeUtils
                    .buildRelativeReferenceTimeOptions(collection.instances)
                    .map((option) => (
                      <MenuItem
                        data-testid={`soundings-ref-time-${option.token}`}
                        key={option.token}
                        value={option.token}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  {referenceTimeUtils
                    .extendCollectionInstances(
                      collection.instances,
                      selectedInstanceId,
                    )
                    .map((instance, currentIndex) => (
                      <MenuItem key={instance.id} value={instance.id}>
                        {getDisplayValue(instance.id)}
                        {currentIndex === collection.instances.length && (
                          <AlertIcon severity="error" />
                        )}
                      </MenuItem>
                    ))}
                </TooltipSelect>
              </ListItem>
            );
          })
        ) : (
          <ListItem>
            <ListItemText primary={t('noServicesFound')} sx={{ pl: 4 }} />
          </ListItem>
        )}
      </List>
    </ToolContainerDraggable>
  );
};
